import { FunctionComponent, useMemo, useCallback } from "react"
import { observer } from "mobx-react-lite"
import { makeStyles } from '@griffel/react'
import { Dropdown, Option } from "@fluentui/react-components"
import type { SelectionEvents, OptionOnSelectData } from "@fluentui/react-components"
import { ArrowSortUpRegular, ArrowSortDownRegular, TextSortAscendingRegular, TextSortDescendingRegular } from "@fluentui/react-icons"
import { useGenericListSettings, parseOrderExpression, IOrder, PARAM_ORDER, getParamId, FilterValueType } from "@knowmax/genericlist-core"

declare module '@knowmax/genericlist-core' {
  interface IOrder {
    /** When set to FilterValueType.string, text-specific sort icons are shown instead of the generic arrows. */
    //fieldType?: FilterValueType
  }
}

const useClasses = makeStyles({
  dropdown: {
    width: '100%',
    minWidth: 0
  },
  dropdownButton: {
    minWidth: 0,
    width: '100%'
  },
  withIcon: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '6px'
  }
})

/** Offers sort order options. Serializes and deserializes in query params of url. */
export const OrderSelector: FunctionComponent<{ list: IOrder[], listId?: string }> = observer(({ list, listId }) => {
  const classes = useClasses()
  const settings = useGenericListSettings()

  const selectedkey = useMemo(() => {
    const ordervalue = settings.params.get(getParamId(PARAM_ORDER, listId))

    if (ordervalue) {
      const [field, descending] = parseOrderExpression(ordervalue)
      const order = list.find((order) => keyForOrder(order.field, order.descending) === keyForOrder(field, descending))

      return order ? keyForOrder(order.field, order.descending) : undefined
    } else {
      const order = list.find((order) => order.default === true)

      return order ? keyForOrder(order.field, order.descending) : undefined
    }
  }, [settings.params, list, listId])

  const options = useMemo(() => {
    return list
      .filter((order) => !order.isAvailable || order.isAvailable(order, settings.params, listId))
      .map((order) => ({
        key: keyForOrder(order.field, order.descending),
        text: order.name,
        data: order
      }))
  }, [list, settings.params, listId])

  const selectedOrder = useMemo(() => options.find(o => o.key === selectedkey)?.data, [options, selectedkey])

  const change = useCallback((_ev: SelectionEvents, data: OptionOnSelectData) => {
    if (data.optionValue) {
      const neworder = list.find((order) => keyForOrder(order.field, order.descending) === data.optionValue)

      const params = new URLSearchParams(settings.params)
      if (neworder) {
        params.set(getParamId(PARAM_ORDER, listId), `${neworder.field}${neworder.descending ? ' desc' : ''}`)
      }
      settings.serialize(params)
    }
  }, [list, settings.params, listId])

  return (
    <Dropdown
      className={classes.dropdown}
      value={selectedOrder?.name ?? ''}
      selectedOptions={selectedkey ? [selectedkey] : []}
      onOptionSelect={change}
      button={{
        className: classes.dropdownButton,
        children: (
          <span className={classes.withIcon}>
            <SortIcon descending={selectedOrder?.descending} fieldType={selectedOrder?.fieldType} />
            {selectedOrder?.name}
          </span>
        )
      }}
    >
      {options.map((option) => (
        <Option key={option.key} value={option.key} text={option.text}>
          <span className={classes.withIcon}>
            <SortIcon descending={option.data.descending} fieldType={option.data.fieldType} />
            {option.text}
          </span>
        </Option>
      ))}
    </Dropdown>
  )
})

const SortIcon = ({ descending, fieldType }: { descending?: boolean, fieldType?: FilterValueType }) => {
  if (fieldType === FilterValueType.string) {
    return descending ? <TextSortDescendingRegular /> : <TextSortAscendingRegular />
  }
  return descending ? <ArrowSortDownRegular /> : <ArrowSortUpRegular />
}

const keyForOrder = (field: string, descending?: boolean) => `${field}${descending === true}`